import { createContext, useState, useContext, useMemo, useCallback } from 'react';
import { PARTS, DEFAULT_CONFIG, BASE_PRICE, isMultiSelect, CATEGORIES } from '../data/parts';

const ConfigContext = createContext();

export function ConfigProvider({ children }) {
    const [config, setConfig] = useState(DEFAULT_CONFIG);

    const updateConfig = (categoryId, partId) => {
        setConfig(prev => {
            // 检查是多选还是单选分类
            if (isMultiSelect(categoryId)) {
                // 多选分类 (comfort, style)
                const current = prev[categoryId] || [];
                const exists = current.includes(partId);

                return {
                    ...prev,
                    [categoryId]: exists
                        ? current.filter(id => id !== partId) // 取消选择
                        : [...current, partId] // 添加选择
                };
            }

            // 单选分类 (trim, body, performance)
            return {
                ...prev,
                [categoryId]: partId
            };
        });
    };

    // 从画廊应用配置到配置器
    const applyConfig = useCallback((configData) => {
        if (!configData) return;

        // 验证并应用配置
        const newConfig = {
            trim: configData.trim || DEFAULT_CONFIG.trim,
            body: configData.body || DEFAULT_CONFIG.body,
            comfort: Array.isArray(configData.comfort)
                ? configData.comfort
                : DEFAULT_CONFIG.comfort,
            performance: configData.performance || DEFAULT_CONFIG.performance,
            style: Array.isArray(configData.style)
                ? configData.style
                : DEFAULT_CONFIG.style,
        };

        setConfig(newConfig);
    }, []);

    // 导出当前配置用于上传
    const exportConfig = useCallback(() => {
        return {
            trim: config.trim,
            body: config.body,
            comfort: config.comfort || [],
            performance: config.performance,
            style: config.style || [],
        };
    }, [config]);

    // 重置为默认配置
    const resetConfig = useCallback(() => {
        setConfig(DEFAULT_CONFIG);
    }, []);

    // 计算总价
    const totalPrice = useMemo(() => {
        let total = BASE_PRICE;

        CATEGORIES.forEach(category => {
            const categoryId = category.id;
            const value = config[categoryId];

            if (isMultiSelect(categoryId)) {
                // 多选分类 - 累加所有选中项的价格
                if (Array.isArray(value)) {
                    value.forEach(itemId => {
                        const part = PARTS[categoryId]?.find(p => p.id === itemId);
                        if (part) total += part.price;
                    });
                }
            } else {
                // 单选分类 - 添加选中项的价格
                const part = PARTS[categoryId]?.find(p => p.id === value);
                if (part) total += part.price;
            }
        });

        return total;
    }, [config]);

    // 获取选中的配件
    const getSelectedPart = (categoryId) => {
        const value = config[categoryId];

        if (isMultiSelect(categoryId)) {
            // 多选分类返回数组
            if (Array.isArray(value)) {
                return value.map(id => PARTS[categoryId]?.find(p => p.id === id)).filter(Boolean);
            }
            return [];
        }

        // 单选分类返回单个对象
        return PARTS[categoryId]?.find(p => p.id === value);
    };

    // 检查配件是否被选中
    const isPartSelected = (categoryId, partId) => {
        const value = config[categoryId];

        if (isMultiSelect(categoryId)) {
            return Array.isArray(value) && value.includes(partId);
        }

        return value === partId;
    };

    return (
        <ConfigContext.Provider value={{
            config,
            updateConfig,
            applyConfig,
            exportConfig,
            resetConfig,
            totalPrice,
            getSelectedPart,
            isPartSelected
        }}>
            {children}
        </ConfigContext.Provider>
    );
}

export function useConfig() {
    return useContext(ConfigContext);
}
